import Image from 'next/image';
import Link from 'next/link';
import { cn } from '../../lib/utils';

export const DEFAULT_LOGO_SRC = '/brand/loamify-wordmark.png';
export const DEFAULT_LOGO_ICON_SRC = '/brand/logo-icon.png';

/** Full wordmark — public/brand/loamify-wordmark.png (1757×406) */
const FULL_LOGO_ASPECT_RATIO = 1757 / 406;
/** Icon mark — public/brand/logo-icon.png (987×985 bento mark) */
const ICON_LOGO_ASPECT_RATIO = 987 / 985;

export interface LogoProps {
  href?: string;
  /** Full wordmark image path */
  src?: string;
  /** Icon-only mark for compact surfaces (e.g. collapsed sidebar) */
  iconSrc?: string;
  /** `icon` renders the square mark; `full` renders the wordmark */
  variant?: 'full' | 'icon';
  className?: string;
  /** Height in px — width scales automatically */
  height?: number;
}

export function Logo({
  href = '/',
  src = DEFAULT_LOGO_SRC,
  iconSrc = DEFAULT_LOGO_ICON_SRC,
  variant = 'full',
  className,
  height = 32,
}: LogoProps) {
  const isIcon = variant === 'icon';
  const imageSrc = isIcon ? iconSrc : src;
  const aspectRatio = isIcon ? ICON_LOGO_ASPECT_RATIO : FULL_LOGO_ASPECT_RATIO;
  const width = isIcon ? height : Math.round(height * aspectRatio);

  const mark = (
    <span
      className={cn(
        'inline-flex transition-transform duration-200 ease-out',
        'hover:scale-105 active:scale-[0.98]',
        'motion-reduce:transition-none motion-reduce:hover:scale-100',
        isIcon ? 'origin-center' : 'origin-left',
      )}
    >
      <Image
        src={imageSrc}
        alt="LOAMIFY"
        width={width}
        height={height}
        unoptimized
        className={cn('block max-w-none', className)}
        style={{ height, width }}
        priority
      />
    </span>
  );

  if (href) {
    return (
      <Link
        href={href}
        className="inline-flex shrink-0 rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {mark}
      </Link>
    );
  }

  return mark;
}
